import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { config } from '@/config';
import { prepareHeaders } from '@/configuration/tokenHandling/prepareHeaders';
import {
    ASSIGNMENTS_ENDPOINT_PATH,
    BRAKE_LINING_SETTING_API_PATH,
    WORKSHOPS_ENDPOINT_PATH,
} from '@/features/common/components/dialogs/dialogConstants';

export type DialogQueryParams = {
    path: typeof WORKSHOPS_ENDPOINT_PATH | typeof ASSIGNMENTS_ENDPOINT_PATH | typeof BRAKE_LINING_SETTING_API_PATH;
    id?: string;
};

export type DialogMutationParams = {
    path: typeof BRAKE_LINING_SETTING_API_PATH;
    data: unknown;
};

export const dialogApi = createApi({
    reducerPath: 'dialogApi',
    baseQuery: fetchBaseQuery({ baseUrl: config.backend.BACKEND_URI, prepareHeaders }),
    endpoints: (build) => ({
        getDialogData: build.query<unknown, DialogQueryParams>({
            query: ({ path, id }) => ({
                url: id ? `${path}/${id}` : path,
            }),
        }),
        putDialogData: build.mutation<unknown, DialogMutationParams>({
            query: ({ path, data }) => ({
                url: path,
                method: 'PUT',
                body: data,
            }),
            /**
             * Optimistically syncs the PUT response back into the `getDialogData` cache
             * for the same `path`, avoiding a redundant refetch after a successful mutation.
             */
            onQueryStarted: async ({ path }, { dispatch, queryFulfilled }) => {
                const { data: updated } = await queryFulfilled;
                dispatch(dialogApi.util.updateQueryData('getDialogData', { path }, () => updated));
            },
        }),
    }),
});

export const { useGetDialogDataQuery, usePutDialogDataMutation } = dialogApi;
